"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-mono text-[11px] uppercase tracking-[0.2em] bg-transparent hover:bg-transparent focus:bg-transparent relative px-0 py-2 group transition-colors duration-300">
                <span className="relative">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8">
        <div className="h-10 w-48 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-56 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-40 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 px-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="font-default text-4xl font-bold text-foreground hover:text-primary transition-colors duration-300 tracking-tight">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2841" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 ease-out ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-transparent border-b border-transparent"}`}>
      <div className={`mx-auto flex items-center justify-between px-6 lg:px-12 transition-all duration-500 ${scrolled ? "py-4" : "py-6 lg:py-8"}`}>
        {}
        <div className={`flex-shrink-0 transition-transform duration-500 ease-out ${scrolled ? "scale-[0.92]" : "scale-100"}`}>
          <Link to="/" aria-label="The Eclipse Home" className="inline-flex">
            <WvcLogo className="h-7 w-auto text-foreground" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="50">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex flex-shrink-0">
          <Button asChild variant="outline" className="border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground rounded-md px-6 py-2 h-auto font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300">
            <Link to="/EclipsePage">Download EPK</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent hover:text-primary" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l-0 p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center justify-between px-6 py-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <WvcLogo className="h-7 w-auto text-foreground" />
                </Link>
              </div>

              <div className="flex-1 flex flex-col justify-center py-12">
                <MenuProvider menu_id="50">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="px-8 pb-12">
                <SheetClose asChild>
                  <Button asChild variant="outline" className="w-full border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground rounded-md py-6 h-auto font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300">
                    <Link to="/EclipsePage">Download EPK</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}